import React, {
    createContext,
    FC,
    useContext,
    useEffect,
    useState
} from 'react';

interface LayerState {
    isActive: boolean;
    type: string;
    hiddenBottomButton: boolean;
    form: React.ReactNode;
    callback: () => void;
}

interface LayerContextValue {
    layerState: LayerState;
    showLayer: (
        type: string,
        hiddenBottomButton: boolean,
        form: React.ReactNode,
        callback?: () => void
    ) => void;
    hideLayer: () => void;
}

// Context 생성
const LayerContext = createContext<LayerContextValue | undefined>(undefined);

// Provider 컴포넌트
export const LayerProvider: FC = ({ children }) => {
    const [layerState, setLayerState] = useState({
        isActive: false,
        type: 'alert', // 'alert' 또는 'confirm'
        form: null,
        callback: () => {}
    });

    // 팝업을 열고 설정을 지정하는 함수
    const showLayer = (
        type: string,
        hiddenBottomButton: boolean,
        form: React.ReactNode,
        callback = () => {}
    ) => {
        setLayerState({
            isActive: true,
            type,
            form,
            hiddenBottomButton,
            callback
        });
    };

    // 팝업을 닫는 함수
    const hideLayer = () => {
        setLayerState((prev) => ({ ...prev, isActive: false }));
    };

    // 레이어 상태 및 제어 함수
    const contextValue = {
        layerState,
        showLayer,
        hideLayer
    };

    useEffect(() => {
        return () => {
            setLayerState({
                isActive: false,
                type: 'alert', // 'alert' 또는 'confirm'
                form: null,
                hiddenBottomButton: false,
                callback: () => {}
            });
        };
    }, []);

    return (
        <LayerContext.Provider value={contextValue}>
            {children}
        </LayerContext.Provider>
    );
};

// Custom Hook (편의성)
export const useLayer = () => {
    const context = useContext(LayerContext);

    // 2. Provider가 없는 경우 명시적으로 에러를 발생시킵니다.
    if (context === undefined) {
        return {
            layerState: {
                isActive: false,
                type: '',
                form: null,
                hiddenBottomButton: false,
                callback: () => {}
            },
            showLayer: () => {},
            hideLayer: () => {}
        };
    }

    return context;
};
